import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js/pure'
import { createContext, useContext, useMemo, useState } from 'react'

import { env } from 'src/env/client.mjs'
import colors from 'src/styles/theme/colors'
import fontFamily from 'src/styles/theme/fontFamily'

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!)

export type PaymentState = {
  succeeded: boolean
  processing: boolean
  handleReset: () => void
  setSucceeded: (succeeded: boolean) => void
  setProcessing: (processing: boolean) => void
}

const PaymentContext = createContext<PaymentState>({} as PaymentState)

export const PaymentProvider = ({
  children,
  amount = '0',
  savePaymentMethod = false,
  isRecurring = false,
  setupOnly = false
}: {
  children: any
  amount?: string
  savePaymentMethod?: boolean
  isRecurring?: boolean
  setupOnly?: boolean
}) => {
  const [succeeded, setSucceeded] = useState<boolean>(false)
  const [processing, setProcessing] = useState<boolean>(false)

  const handleReset = () => {
    setProcessing(false)
    setSucceeded(false)
  }

  const memoisedValue = useMemo(
    () => ({
      succeeded,
      processing,
      setSucceeded,
      setProcessing,
      handleReset
    }),
    [succeeded, processing]
  )

  const setupFutureUsage = useMemo(() => {
    if (isRecurring) return 'off_session'
    if (savePaymentMethod) return 'on_session'
    return undefined
  }, [isRecurring, savePaymentMethod])

  const paymentMethodCreationOptions: {
    paymentMethodCreation?: 'manual' | undefined
  } = setupOnly ? { paymentMethodCreation: 'manual' } : {}

  const mode = useMemo(() => {
    if (isRecurring) return 'subscription'
    if (setupOnly) return 'setup'
    return 'payment'
  }, [isRecurring, setupOnly])

  const calculatedAmount = useMemo(() => {
    if (setupOnly) return undefined
    if (amount === '0') return 1000
    return parseFloat(amount) * 100
  }, [amount, setupOnly])

  const elementOptions = useMemo(() => {
    const baseOptions: any = {
      fonts: [
        {
          cssSrc:
            'https://fonts.googleapis.com/css2?family=Geist:wght@100..900&display=swap'
        }
      ],
      appearance: {
        theme: 'stripe',
        variables: {
          fontFamily: fontFamily.bodyRebrand.join(','),
          borderRadius: '0px',
          colorText: colors.shark,
          colorDanger: colors.wineRich
        },
        rules: {
          '.Input': { boxShadow: 'none', borderColor: colors.silver },
          '.Input--invalid': {
            boxShadow: 'none',
            borderColor: colors.wineRich,
            color: colors.wineRich
          },
          '.Error': { color: colors.wineRich }
        }
      },
      mode,
      currency: 'nzd',
      captureMethod: setupOnly ? 'manual' : 'automatic',
      ...paymentMethodCreationOptions
    }

    if (calculatedAmount !== undefined) {
      baseOptions.amount = calculatedAmount
    }

    if (
      setupFutureUsage === 'on_session' ||
      setupFutureUsage === 'off_session'
    ) {
      baseOptions.setupFutureUsage = setupFutureUsage
    }

    return baseOptions
  }, [
    mode,
    calculatedAmount,
    setupFutureUsage,
    setupOnly,
    paymentMethodCreationOptions
  ])

  return (
    stripePromise && (
      <Elements options={elementOptions} stripe={stripePromise}>
        <PaymentContext.Provider value={memoisedValue}>
          {children}
        </PaymentContext.Provider>
      </Elements>
    )
  )
}

export const usePayment = () => useContext(PaymentContext)
